import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {Button, inputStyles, TextField} from "../../../../Components/Inputs";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {useInput} from "../../../../Components/Inputs/inputUtility";
import {LinkButton, ExternalLink, EmailLink} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import {allow} from "../../../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {isObjectNullOrEmpty} from "../../../../Utilities/Types/objectUtilities";
import {aphCatalogApi} from "./aphCatalogApi";
import {AphSearchResultsView} from "./AphSearchResultsView";
import {emptyGuid} from "../../../../Utilities/commonUtilities";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {aphApi} from "../aphApi";
import {aphCatalogItemModel} from "../AphCatalogItem/aphCatalogItemFactory";
import { isFimcAdmin } from "../../../../Utilities/LocalStorage/storageUtilities";


export const AphCatalogContainer = ({
                                        aphQuotaDates,
                                        aphSearchCriteria,
                                        handleApiCall,
                                        handleSetAphSearchCriteria,
                                        isLoading,
                                        redirect,
                                        systemType,                                        
                                    }: ContextProps) => {

    const [aphResources, setAphResources] = useState<Record<string, unknown>[]>([]);
    const [initialAphResources, setInitialAphResources] = useState<Record<string, unknown>[]>([]);
    const [aphTotalRecords, setAphTotalRecords] = useState(0);
    const [updatedAvailabilityDropdowns, setUpdatedAvailabilityDropdowns] = useState<string[]>([]);


    const keywordSearch = useInput("");
    const barCodeSearch = useInput("");

    const canManageAphCatalogItems = allow(PolicyEvents.MANAGE_APH_CATALOG_ITEMS)
    const {isAphQuotaWindow} = aphQuotaDates;

    const loadAphResourcesResult = (result: { aphCatalogItems: Record<string, unknown>[]; totalRecords: number }) => {
        setAphResources(result.aphCatalogItems);
        setInitialAphResources(result.aphCatalogItems);
        setAphTotalRecords(result.totalRecords);
    };

    const onAvailabilityStatusClick = (resourceId: string, value: string | number) => {
        setAphResources(aphResources.map((resource: Record<string, unknown>) =>
            resource.id === resourceId
                ? {
                    ...resource,
                    availabilityStatus: value,
                }
                : resource
        ));
    }

    const handleSaveAvailabilityStatus = () => {

        const aphItemsToUpdate = aphResources.filter((aphResource: Record<string, unknown>) => {
            const initialAphResource = initialAphResources.find((initial: Record<string, unknown>) => initial.id === aphResource.id);
            return initialAphResource && initialAphResource.availabilityStatus !== aphResource.availabilityStatus;
        }).map(aphCatalogItemModel);


        if (aphItemsToUpdate.length > 0) {
            handleApiCall(() => aphCatalogApi.updateAphCatalogAvailability(aphItemsToUpdate), getAphResources);
        }
    }

    const getAphResources = () => {
        if (isObjectNullOrEmpty(aphSearchCriteria)) return;

        const updatedCriteria = {...aphSearchCriteria, title: keywordSearch.value, loadSearchResults: true}
        handleApiCall(() => aphCatalogApi.getAphCatalogResources(updatedCriteria), (result: { aphCatalogItems: Record<string, unknown>[]; totalRecords: number }) => loadAphResourcesResult(result));

    };

    const handleEdit = (aphCatalogId: string) => {
        redirect(locations.APH_CATALOG_ITEM.getLink([aphCatalogId, systemType]).to);
    }

    const handleAddToQueue = async (aphCatalogId: string) => {
        handleApiCall(() => aphApi.addAphQueueItem(aphCatalogId));
    }


    const handleSearch = () => {
        if (isObjectNullOrEmpty(aphSearchCriteria)) return;

        const updatedCriteria = {
            ...aphSearchCriteria,
            title: keywordSearch.value,
            catalogNumber: barCodeSearch.value,
        };

        handleApiCall(
            () => aphCatalogApi.getAphCatalogResources(updatedCriteria), (result: { aphCatalogItems: Record<string, unknown>[]; totalRecords: number }) => loadAphResourcesResult(result)
        );
    };

    const noResults = isArrayNullOrEmpty(aphResources);

    const buttonSharedSettings = {
        medium_columns: gridConstants.column.THREE,
        small_columns: gridConstants.column.SIX,
    };

    useEffect(() => {
        if (!aphSearchCriteria) return;

        if (aphSearchCriteria.loadSearchResults)
            getAphResources();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aphSearchCriteria, systemType, keywordSearch.value]);

    useEffect(() => {
        const changedIds = aphResources
            .filter((resource: Record<string, unknown>) => {

                const initialAphResource = initialAphResources.find((r: Record<string, unknown>) => r.id === resource.id);
                return initialAphResource && String(initialAphResource.availabilityStatus) !== String(resource.availabilityStatus);
            })
            .map((r: Record<string, unknown>) => r.id as string);

        setUpdatedAvailabilityDropdowns(changedIds);
    }, [aphResources, initialAphResources]);

    if (!aphSearchCriteria) return null;

    return (
        <>
            <PageTitle h1={`APH`}/>
            {(!isAphQuotaWindow || isFimcAdmin()) && (

                <>
                    <GridContainer>
                        <GridCell>
                            <p>
                                Please visit the APH website (
                                <ExternalLink url="https://www.aph.org/shop/" label="https://www.aph.org/shop/"/>
                                ) to view the catalog of items with current descriptions and
                                availability. As an Ex-Officio Trustee of APH and to maximize
                                available Federal Quota Funds, the APH products shown on this site
                                will differ from what you see on the APH website. If you have
                                specific questions about a product or order, please contact{" "}
                                <EmailLink address="information@fimcvi.org" className="-email"/>.
                            </p>
                        </GridCell>
                    </GridContainer>

                    <div className="searchBox margin--bottom">
                        <div className="searchBox__title">Search the catalog</div>
                        <GridContainer hasMarginX hasMarginY className="searchBox__input">
                            <TextField
                                label={`keyword Search box`}
                                labelClass={`show-for-sr`}
                                large_columns={gridConstants.column.TEN}
                                medium_columns={gridConstants.column.EIGHT}
                                name={`txtSearch`}
                                placeholder={`Title Keyword`}
                                {...keywordSearch}
                            />

                            <TextField
                                label="barCode Search box"
                                labelClass="show-for-sr"
                                large_columns={gridConstants.column.TEN}
                                medium_columns={gridConstants.column.EIGHT}
                                name="barCodeSearch"
                                placeholder="Catalog Number"
                                {...barCodeSearch}
                            />


                            <GridCell large_columns={gridConstants.column.TWO} medium_columns={gridConstants.column.FOUR}>
                                <Button
                                    buttonType={inputStyles.button.types.SEARCH}
                                    label="Search"
                                    name="btnSearch"
                                    onClick={handleSearch}
                                    isPrimary
                                />
                            </GridCell>
                        </GridContainer>
                    </div>

                    <GridContainer>
                        <GridCell className="text-center">
                            <LinkButton {...locations.APH_WHATS_NEW.getLink()} />
                            <LinkButton {...locations.APH_QUEUE.getLink()} />
                            <LinkButton {...locations.APH_LOAN_HISTORY.getLink()} />
                            {
                                canManageAphCatalogItems &&
                                <LinkButton
                                    {...locations.APH_CATALOG_ITEM.getLink([emptyGuid, systemType])}
                                    label="Add New Item"
                                />
                            }
                            {
                                isFimcAdmin() && (
                                   <LinkButton
                                {...locations.LIBRARY_SHIPPING_LOCATIONS.getLink([undefined, systemType])}
                            /> 
                            )}
                            
                        </GridCell>
                    </GridContainer>

                    <LayoutFullBleed className="catalog">
                        <header>
                            <h2>Search Results</h2>
                        </header>
                        <GridContainer isReverseOrder className={`form__header`}>
                            {!noResults && canManageAphCatalogItems &&
                                <GridCell {...buttonSharedSettings}>
                                    <>
                                        <Button
                                            label={"Save Availability Status Changes"}
                                            name={"btnSaveAvailabilityStatus"}
                                            onClick={handleSaveAvailabilityStatus}
                                            isPrimary
                                            disabled={isLoading}
                                        />
                                    </>
                                </GridCell>
                            }
                        </GridContainer>

                        {aphResources && !noResults && (
                            <AphSearchResultsView
                                aphResources={aphResources}
                                aphSearchCriteria={aphSearchCriteria}
                                canManageAphCatalogItems={canManageAphCatalogItems}
                                handleAddToQueue={handleAddToQueue}
                                handleEdit={handleEdit}
                                handleSetAphSearchCriteria={handleSetAphSearchCriteria}
                                redirect={redirect}
                                totalRecords={aphTotalRecords}
                                onAvailabilityStatusClick={onAvailabilityStatusClick}
                                updatedAvailabilityDropdowns={updatedAvailabilityDropdowns}
                            />
                        )}
                        {noResults && (
                            <p className="text-center">
                                No results. Please contact FIMC-VI if you believe this is in error. Include
                                information@FIMCVI.org in the message.
                            </p>
                        )}
                    </LayoutFullBleed>

                </>
            )}

            {(isAphQuotaWindow && !isFimcAdmin()) && (

                <p>FIMC-VI has spent our federal quota funds for APH&apos;s October - September fiscal year. This form will reopen on October 1, when we receive the new fiscal year&apos;s funding. Please return at that time to submit orders. Contact Colleen Mulcahey at <EmailLink address="cmulcahey@fimcvi.org" className="-email"/> with any questions.</p>
                
            )}

        </>
    );
};

